import { Preload } from "@/components/preload";
import { useIsMobile } from "@/hooks/use-mobile";
import { WorkspaceLink } from "@/lib/router/workspace-link";
import { cn } from "@/lib/utils";
import { UrlPattern } from "@/utils/url";
import { Loading } from "@backpac/monica";
import {
  Button,
  ButtonDropdownIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SidebarTrigger,
  Typography,
  useSidebar,
} from "@backpac/monica";
import { type ReactNode, Suspense } from "react";
import { useLocation } from "react-router";

export function PageContainer({
  nav,
  children,
  className,
}: {
  nav?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <>
      {nav}
      <div className={cn("p-4 mb-16 desktop:p-8 max-w-container min-w-[240px] mx-auto box-content", className)}>
        <Suspense fallback={<Loading.FullHeight />}>{children}</Suspense>
      </div>
    </>
  );
}

PageContainer.Navigation = function Navigation({
  title,
  links,
}: { title: ReactNode; links?: { to: string; text: string }[] }) {
  const { openMobile } = useSidebar();
  const isMobile = useIsMobile();
  const currentPath = useLocation().pathname;

  return (
    <div className="flex item-center px-4 desktop:px-6 py-4 max-h-16 border-b border-borderLight bg-white z-[49] w-[-webkit-fill-available]">
      <Preload hrefs={links?.map((link) => link.to) ?? []} as="document" />
      {typeof title === "string" ? <PageContainer.Title>{title}</PageContainer.Title> : title}
      {links &&
        links.length > 0 &&
        (isMobile ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="secondary" rightAddon={<ButtonDropdownIcon />}>
                {links.find((link) => new UrlPattern(link.to).match(currentPath))?.text}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="border boder-boderLight rounded-[12px] bg-white p-1 max-w-[160px]">
              {links.map((link) => (
                <WorkspaceLink key={link.to} to={link.to}>
                  <Typography size="xs" weight={500} className="py-2 px-4">
                    {link.text}
                  </Typography>
                </WorkspaceLink>
              ))}
            </PopoverContent>
          </Popover>
        ) : (
          <div className="flex items-center gap-2">
            {links.map((link) => (
              <NavLinkButton key={link.to} to={link.to}>
                {link.text}
              </NavLinkButton>
            ))}
          </div>
        ))}
      <SidebarTrigger
        className={cn("block ml-auto md:hidden", {
          "bg-[#FFEFE0]": openMobile,
        })}
      />
    </div>
  );
};

PageContainer.Title = function Title({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Typography font="unbounded" size="lg" className={cn("mr-6", className)}>
      {children}
    </Typography>
  );
};

function NavLinkButton({ children, to }: { children: React.ReactNode; to: string }) {
  const { pathname } = useLocation();
  const isActive = new UrlPattern(to).match(pathname) !== null;

  return (
    <WorkspaceLink to={to}>
      <Button variant={isActive ? "secondary" : "ghost"} className={cn({ "text-textMuted": !isActive })}>
        {children}
      </Button>
    </WorkspaceLink>
  );
}
